'use client';

import { useEffect, useRef, useState, type JSX } from 'react';
import type { AddressAutocompletePrediction, AddressObject } from '@cfixe-monorepo/shared';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

/** « 12 Rue de la République, 69001 Lyon, France » → primaire / secondaire (comme ZoneAddressStep). */
function splitPredictionDescription(description: string): { primary: string; secondary?: string } {
  const commaIndex = description.indexOf(',');
  if (commaIndex === -1) return { primary: description };
  return { primary: description.slice(0, commaIndex), secondary: description.slice(commaIndex + 1).trim() };
}

export interface RideAddressFieldProps {
  id: string;
  label: string;
  placeholder: string;
  searchHint: string;
  value?: AddressObject;
  onChange: (address: AddressObject) => void;
  onAutocomplete: (query: string) => Promise<AddressAutocompletePrediction[]>;
  onResolveDetails: (placeId: string) => Promise<AddressObject>;
  /**
   * Garde de sélection (refus inline) : appelée avec l'adresse résolue AVANT de la commettre.
   * Renvoie un message d'erreur → la sélection est REFUSÉE (l'input restaure l'adresse
   * précédente, le message s'affiche sous le champ) ; null → sélection acceptée. Une erreur
   * de la promesse vaut acceptation (garde consultative — le devis/checkout restent l'autorité).
   */
  validateSelection?: (address: AddressObject) => Promise<string | null>;
  /**
   * Erreur externe (ex. zone invalidée par un changement d'HEURE, pas d'adresse) affichée sous
   * le champ : l'adresse déjà commise reste en place, seul le message signale le conflit.
   */
  errorText?: string;
  /**
   * Adresse verrouillée par le pro (trajet par défaut d'une carte « Course », produit type
   * navette) : champ en lecture seule, autocomplete inactif.
   */
  disabled?: boolean;
  /** Mention affichée sous le champ verrouillé (ex. « Adresse fixe »). */
  lockedHint?: string;
}

/**
 * Champ adresse avec autocomplete Google (proxy backend) du tunnel VTC.
 * UI calquée sur ZoneAddressStep (`bt-zone-address__*`) — ZoneAddressStep lui-même est
 * couplé au BookingContext, d'où cette version props-driven.
 */
export function RideAddressField({
  id,
  label,
  placeholder,
  searchHint,
  value,
  onChange,
  onAutocomplete,
  onResolveDetails,
  validateSelection,
  errorText,
  disabled = false,
  lockedHint,
}: RideAddressFieldProps): JSX.Element {
  const [query, setQuery] = useState<string>(value?.formatted_address ?? '');
  const [predictions, setPredictions] = useState<AddressAutocompletePrediction[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState<boolean>(false);
  const [resolving, setResolving] = useState<boolean>(false);
  // Message de REFUS de la dernière sélection (garde validateSelection) : effacé dès que le
  // client retape ou retente une sélection.
  const [selectionError, setSelectionError] = useState<string | undefined>(undefined);
  // Requête déjà résolue (sélection) : évite de rouvrir le panneau quand le parent resynchronise `value`.
  const selectedQueryRef = useRef<string | undefined>(value?.formatted_address);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestIdRef = useRef<number>(0);

  // Le parent peut reposer/effacer la valeur (reset formulaire) → resynchronise l'affichage.
  useEffect((): void => {
    setQuery(value?.formatted_address ?? '');
    selectedQueryRef.current = value?.formatted_address;
  }, [value]);

  useEffect((): (() => void) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();

    if (disabled || resolving || trimmed.length < MIN_QUERY_LENGTH || selectedQueryRef.current === trimmed) {
      setPredictions([]);
      setLoadingPredictions(false);
      return (): void => undefined;
    }

    const requestId = ++requestIdRef.current;
    setLoadingPredictions(true);
    debounceRef.current = setTimeout((): void => {
      onAutocomplete(trimmed)
        .then((result): void => {
          if (requestIdRef.current !== requestId) return;
          setPredictions(result);
        })
        .catch((): void => {
          if (requestIdRef.current !== requestId) return;
          setPredictions([]);
        })
        .finally((): void => {
          if (requestIdRef.current !== requestId) return;
          setLoadingPredictions(false);
        });
    }, DEBOUNCE_MS);

    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, resolving, onAutocomplete, disabled]);

  const selectPrediction = (prediction: AddressAutocompletePrediction): void => {
    setPredictions([]);
    setSelectionError(undefined);
    setResolving(true);
    onResolveDetails(prediction.place_id)
      .then(async (address): Promise<void> => {
        // Garde de sélection (ex. zone de prise en charge) : un message = modification REFUSÉE,
        // l'input restaure l'adresse précédente. Un échec de la garde vaut acceptation
        // (consultatif — le devis/checkout restent l'autorité).
        const refusal = validateSelection ? await validateSelection(address).catch((): null => null) : null;
        if (refusal) {
          setSelectionError(refusal);
          setQuery(value?.formatted_address ?? '');
          selectedQueryRef.current = value?.formatted_address;
          return;
        }
        selectedQueryRef.current = address.formatted_address;
        setQuery(address.formatted_address);
        onChange(address);
      })
      .catch((): void => {
        // Échec de résolution (place_id expiré, réseau) : on restaure la sélection précédente —
        // ne jamais laisser l'input affirmer une adresse que le parent n'a pas.
        setQuery(value?.formatted_address ?? '');
      })
      .finally((): void => setResolving(false));
  };

  const displayedError = selectionError ?? (disabled ? undefined : errorText);

  return (
    <div className="bt-field">
      <label className="bt-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="bt-zone-address">
        <div className="bt-zone-address__field">
          <input
            id={id}
            type="text"
            name={id}
            className={`bt-zone-address__input ${displayedError ? 'bt-zone-address__input--error' : ''}`}
            placeholder={placeholder}
            value={query}
            onChange={(event): void => {
              selectedQueryRef.current = undefined;
              requestIdRef.current += 1;
              setQuery(event.target.value);
              setSelectionError(undefined);
            }}
            disabled={disabled || resolving}
            aria-invalid={!!displayedError}
            aria-describedby={displayedError ? `${id}-error` : undefined}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          {(loadingPredictions || resolving) && (
            <span className="bt-zone-address__input-spinner" role="status">
              <span className="bt-spinner bt-spinner--inline" />
            </span>
          )}
        </div>

        {/* Adresse verrouillée par le pro : mention « Adresse fixe » à la place des hints de saisie. */}
        {disabled && lockedHint && <p className="bt-zone-address__hint">{lockedHint}</p>}

        {!disabled && query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
          <p className="bt-zone-address__hint">{searchHint}</p>
        )}

        {displayedError && (
          <div id={`${id}-error`} className="bt-zone-address__error" role="alert">
            {displayedError}
          </div>
        )}

        {!disabled && predictions.length > 0 && !resolving && (
          <div className="bt-zone-address__panel">
            <ul className="bt-zone-address__list">
              {predictions.map((prediction): JSX.Element => {
                const description = splitPredictionDescription(prediction.description);
                return (
                  <li key={prediction.place_id}>
                    <button
                      type="button"
                      className="bt-zone-address__item"
                      onClick={(): void => selectPrediction(prediction)}
                    >
                      <span className="bt-zone-address__item-main">{description.primary}</span>
                      {description.secondary && (
                        <span className="bt-zone-address__item-secondary">{description.secondary}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
